import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { EASE, Reveal } from "@/components/Reveal";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Field, Honeypot, inputCls } from "@/components/forms";

const HEARD_OPTIONS = [
  "A member of Syndeō",
  "A personal referral",
  "Press or an article",
  "Social media",
  "Search",
  "Other",
];

const initial = {
  full_name: "",
  email: "",
  city: "",
  referrer: "",
  heard_about: "",
  phone: "",
  website: "",
};

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: string) => (e: any) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/applications", {
        ...form,
        referrer: form.referrer || null,
        phone: form.phone || null,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err: any) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? "Too many attempts. Please wait a few minutes and try again."
          : status === 422
            ? "Please complete every required field before submitting."
            : "Something went quiet on our end. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="apply-page" className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-xl px-6 pb-28 pt-36">
        {done ? (
          <motion.div
            data-testid="apply-confirmation"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="pt-16 text-center"
          >
            <img
              src="/assets/o-mark.jpeg"
              alt="ō"
              className="mx-auto h-12 w-12 rounded-full object-cover"
            />
            <div className="mx-auto mt-8 h-px w-10 bg-ochre" />
            <h1 className="mt-8 font-garamond text-4xl text-ink">
              Request received.
            </h1>
            <p className="mx-auto mt-6 max-w-md font-garamond text-lg leading-relaxed text-softblack/85">
              Thank you, {form.full_name.split(" ")[0]}. Your request will
              be reviewed with care, and a confirmation is on its way to{" "}
              {form.email}. If membership aligns, you will hear from us
              quietly.
            </p>
            <Link
              to="/"
              data-testid="apply-return-home"
              className="mt-12 inline-block border-b border-ink pb-1 font-oswald text-[11px] uppercase tracking-[0.3em] text-ink transition-colors hover:border-walnut hover:text-walnut"
            >
              Return to syndeō
            </Link>
          </motion.div>
        ) : (
          <>
            <Reveal>
              <div
                data-testid="login-strip"
                className="mb-14 flex items-center justify-between bg-ink px-6 py-5"
              >
                <p className="font-garamond text-lg text-paper">
                  Already have access?
                </p>
                <Link
                  to="/login"
                  data-testid="apply-login"
                  className="border-b border-paper pb-1 font-oswald text-xs uppercase tracking-[0.3em] text-paper transition-colors duration-300 hover:border-ochre hover:text-ochre"
                >
                  Log in
                </Link>
              </div>
              <p className="font-oswald text-[11px] uppercase tracking-[0.4em] text-seaglass">
                Membership
              </p>
              <h1 className="mt-4 font-garamond text-4xl text-ink sm:text-5xl">
                Request Access
              </h1>
              <p className="mt-6 font-garamond text-lg leading-relaxed text-softblack/85">
                A concise request, reviewed with care. Membership is
                limited and extended by invitation.
              </p>
            </Reveal>

            <form
              data-testid="apply-form"
              onSubmit={submit}
              className="relative mt-14 space-y-8"
            >
              <Honeypot
                value={form.website}
                onChange={(v) => setForm((f) => ({ ...f, website: v }))}
              />
              <Field label="Full name" htmlFor="full_name">
                <input
                  id="full_name"
                  data-testid="apply-full-name"
                  required
                  value={form.full_name}
                  onChange={set("full_name")}
                  className={inputCls}
                />
              </Field>
              <Field label="Email" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  data-testid="apply-email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                />
              </Field>
              <Field label="City / Location" htmlFor="city">
                <input
                  id="city"
                  data-testid="apply-city"
                  required
                  value={form.city}
                  onChange={set("city")}
                  className={inputCls}
                />
              </Field>
              <Field label="Who referred or invited you?" htmlFor="referrer" optional>
                <input
                  id="referrer"
                  data-testid="apply-referrer"
                  value={form.referrer}
                  onChange={set("referrer")}
                  className={inputCls}
                />
              </Field>
              <Field label="How did you hear about Syndeō?" htmlFor="heard_about">
                <select
                  id="heard_about"
                  data-testid="apply-heard-about"
                  required
                  value={form.heard_about}
                  onChange={set("heard_about")}
                  className={inputCls}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  {HEARD_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Phone" htmlFor="phone" optional>
                <input
                  id="phone"
                  type="tel"
                  data-testid="apply-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  className={inputCls}
                />
              </Field>

              {error && (
                <p
                  data-testid="apply-error"
                  className="border border-walnut/40 bg-chalk px-4 py-3 font-garamond text-base text-walnut"
                >
                  {error}
                </p>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  data-testid="apply-submit"
                  disabled={submitting}
                  className="border border-ink px-10 py-4 font-oswald text-xs uppercase tracking-[0.35em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            </form>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
